//Semi-locked

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Pagination,
    CircularProgress,
    Stack,
    Tabs,
    Tab
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import config from '../config';
import { boatListValidator } from '../utils/api/boatValidators';
import { useAuth } from '../hooks/auth/useAuth'
import { FormatBold } from '@mui/icons-material';

// Styling constants
const CARD_HEIGHT = '48px';
const GRID_MAX_WIDTH = '800px';
const FONT_SIZE = '16px';

const SearchInput = styled(TextField)(({ theme }) => ({
    backgroundColor: 'white',
    flex: 1,
    maxWidth: '400px',
    '& .MuiInputBase-input': {
        fontSize: FONT_SIZE,
        height: '20px', // Adjust input height
    },
    '& .MuiInputLabel-root': {
        fontSize: FONT_SIZE,
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: theme.palette.orange.main, // Orange outline
            borderWidth: '2px',
        },
        '&:hover fieldset': {
            borderColor: theme.palette.orange.main, // Keep orange on hover
            borderWidth: '2px',
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.orange.main, // Keep orange on focus
            borderWidth: '2px',
        }
    }
}));

type Boat = {
    BoatId: number;
    BoatModel: string;
    Boat_UrlId?: string;  // SEO-friendly URL identifier
    I: number;
    J: number;
    P: number;
    E: number;
};

interface BoatSelectionProps {
    onSelect?: (boatId: number, boatUrlId?: string) => void;
}

const BoatSelection: React.FC<BoatSelectionProps> = ({ onSelect }) => {
    const [boats, setBoats] = useState<Boat[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [mode, setMode] = useState<'search' | 'browse'>('search');
    const [currentLetter, setCurrentLetter] = useState('A');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const boatsPerPage = 30;
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [pageLoading, setPageLoading] = useState(true);
    const { user, isInitializing } = useAuth();

    useEffect(() => {
        if (!isInitializing) {
            setPageLoading(false);
        }
    }, [isInitializing]);

    const fetchBoats = useCallback(async () => {
        // Cancel any ongoing requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        setLoading(true);
        setError(null);

        // Only fetch if in browse mode or if there's a search term
        if (mode === 'browse' || (mode === 'search' && searchTerm.trim().length > 0)) {
            const params = new URLSearchParams({
                boatId: '-1',
                boatModel: mode === 'search' ? searchTerm : currentLetter,
                page: page.toString(),
                limit: boatsPerPage.toString()
            });

            const baseUrl = config.API_URL;  // Ensure this is correctly set up
            const url = `${baseUrl}/api/boats?${params.toString()}`;

            try {
                const response = await fetch(url, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    signal: abortControllerRef.current.signal
                });

                const data = await handleApiResponse(response);
                setBoats(data.boats || []);
                setTotalPages(data.totalPages || 1);
            } catch (error: unknown) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return; // Ignore aborted requests
                }
                console.error("Failed to fetch boats:", error);
                if (error instanceof Error) {
                    setError(`Failed to fetch boats: ${error.message}`);
                } else {
                    setError('Failed to fetch boats: An unknown error occurred');
                }
                setBoats([]);
                setTotalPages(1);
            }
        } else {
            // Clear boats if in search mode with no search term
            setBoats([]);
            setTotalPages(1);
        }
        setLoading(false);
    }, [mode, searchTerm, currentLetter, page, boatsPerPage]); // Added dependencies

    // Now useEffect will recognize fetchBoats as stable
    useEffect(() => {
        fetchBoats();

        // Cleanup function
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchBoats]); // Only runs when fetchBoats changes

    //Modified 5/15
    //useEffect(() => {
    //    if (mode === 'search') {
    //        const delayDebounceFn = setTimeout(() => {
    //            setPage(1);
    //            fetchBoats();
    //        }, 300);
    //        return () => clearTimeout(delayDebounceFn);
    //    }
    //}, [searchTerm, fetchBoats, mode]);

    useEffect(() => {
        if (mode === 'search') {
            const delayDebounceFn = setTimeout(() => {
                // Only reset page when searchTerm actually changes
                setPage(1);
                // Don't call fetchBoats() here
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchTerm, mode]); // Remove fetchBoats from dependencies

    useEffect(() => {
        fetchBoats();
    }, [fetchBoats]);
    // end modified 5/15

    // Check for boat search string in URL on component mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const boatSearchParam = params.get('boatSearch');

        if (boatSearchParam) {
            setMode('search');
            setSearchTerm(boatSearchParam);
        }
    }, [location.search]);

    const handleApiResponse = async (response: Response) => {
        const clonedResponse = response.clone();

        if (!response.ok) {
            let errorMessage: string;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
            } catch {
                const errorText = await clonedResponse.text();
                errorMessage = `Server error (${response.status}): ${errorText.slice(0, 100)}`;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        try {
            return boatListValidator.validate(data);
        } catch (validationErrors) {
            console.error('Response validation failed:', validationErrors);
            throw new Error('Invalid response format from server');
        }
    };

    const handleModeChange = (newMode: 'search' | 'browse') => {
        setMode(newMode);
        setPage(1);
        if (newMode === 'browse') {
            setCurrentLetter('A');
        }
    };

    const handleLetterChange = (letter: string) => {
        setCurrentLetter(letter);
        setPage(1);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleBoatSelect = (boat: Boat) => {
        // Use SEO-friendly URL if available, otherwise use numeric ID
        const identifier = boat.Boat_UrlId || boat.BoatId;
        if (onSelect) {
            onSelect(boat.BoatId, boat.Boat_UrlId);
        } else {
            navigate(`/quoter/sail-selection/${identifier}`);
        }
    };

    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
                Boat Selection
            </Typography>

            <Box sx={{
                maxWidth: GRID_MAX_WIDTH,
                margin: '0 auto',
                borderBottom: 1,
                borderColor: 'divider'
            }}>
                <Tabs
                    value={mode}
                    onChange={(e, newMode) => handleModeChange(newMode)}
                    aria-label="search options"
                >
                    <Tab label="SEARCH" value="search" />
                    <Tab label="BROWSE" value="browse" />
                </Tabs>
            </Box>

            {/* Search Box - Below tabs */}
            {mode === 'search' && (
                <Box sx={{
                    py: 2,
                    maxWidth: GRID_MAX_WIDTH,
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <SearchInput
                        placeholder="Search Boats"
                        autoComplete="off"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                    />
                </Box>
            )}

            {mode === 'browse' && (
                <Box sx={{
                    mb: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    maxWidth: GRID_MAX_WIDTH,
                    margin: '0 auto'
                }}>
                    {alphabet.map((letter) => (
                        <Button
                            key={letter}
                            onClick={() => handleLetterChange(letter)}
                            variant={currentLetter === letter ? 'contained' : 'outlined'}
                            sx={{
                                minWidth: '40px',
                                fontSize: FONT_SIZE
                            }}
                        >
                            {letter}
                        </Button>
                    ))}
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {error ? (
                        <Typography color="error" sx={{ fontSize: FONT_SIZE, textAlign: 'center', mt: 2 }}>
                            {error}
                        </Typography>
                    ) : (
                        <Box sx={{
                            width: '100%',
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Stack spacing={1} sx={{ width: GRID_MAX_WIDTH }}>
                                {boats.map((boat, index) => (
                                    <Card
                                        key={boat.BoatId}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            },
                                            height: CARD_HEIGHT,
                                            backgroundColor: index % 2 === 0 ? theme.palette.customColors.result || '#ffffff' :
                                                theme.palette.customColors.resultAlt || '#f5f5f5'
                                        }}
                                        onClick={() => handleBoatSelect(boat)}
                                    >
                                        <CardContent sx={{
                                            py: '8px !important',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                width: '100%',
                                                alignItems: 'center',
                                                flexDirection: { xs: 'column', sm: 'row' }
                                            }}>
                                                <Box sx={{
                                                    flex: { xs: '1 0 auto', sm: '0 0 40%' },
                                                    width: { xs: '100%', sm: 'auto' },
                                                    mb: { xs: 1, sm: 0 } // Add margin bottom on mobile
                                                }}>
                                                    <Typography sx={{ fontSize: FONT_SIZE, fontWeight: 'bold' }} noWrap>
                                                        {boat.BoatModel}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{
                                                    flex: { xs: '1 0 auto', sm: '0 0 60%' },
                                                    width: { xs: '100%', sm: 'auto' },
                                                    display: 'flex',
                                                    justifyContent: 'space-around',
                                                    pl: { xs: 0, sm: 3 } // No left padding on mobile
                                                }}>
                                                    {['I', 'J', 'P', 'E'].map((measurement) => (
                                                        <Typography
                                                            key={measurement}
                                                            sx={{
                                                                fontSize: FONT_SIZE,
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {measurement}: {boat[measurement as keyof Pick<Boat, 'I' | 'J' | 'P' | 'E'>]}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}

                                {boats.length === 0 && (
                                    <Typography sx={{ mt: 2, fontSize: FONT_SIZE, textAlign: 'center' }}>
                                        {mode === 'search' && !searchTerm.trim()
                                            ? 'Enter a boat name to search, or select Browse to view alphabetically.'
                                            : 'No results found. Try a different search term or select Browse.'}
                                    </Typography>
                                )}

                                {boats.length > 0 && (
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={handlePageChange}
                                            color="primary"
                                            size="large"
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    fontSize: FONT_SIZE
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default BoatSelection;